import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

const emailRegExp =
  /^[A-Za-z0-9_+%-]+(\.[A-Za-z0-9_+%-]+)*@[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,24}$/;

const invalidPatterns = [
  ' ',
  '..',
  '@-',
  '-@',
  '_@',
  '@_',
  '@.',
  '.@',
  '-.',
  '_.',
  '._',
];

export const emailValidator = (): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (
      value &&
      (!emailRegExp.test(value) ||
        value.includes(' ') ||
        value.split('@').length !== 2 ||
        invalidPatterns.some((pattern) => value.includes(pattern)))
    ) {
      return { emailInvalid: true };
    }
    return null;
  };
};

export const cardNumberValidator = (): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }

    const validCardLength = 16;
    const cleanPan = cleanStringFormSpaces(control.value);
    const isValid =
      cleanPan.length === validCardLength && luhnCheck(control.value);
    if (!isValid) {
      return { cardInvalid: true };
    }

    return null;
  };
};

/**
 * The `_cleanStringFormSpaces` method is used to clean a string by removing all spaces.
 *
 * @param {string} value - The string to be cleaned from spaces.
 * @returns {string} - Returns the cleaned string from spaces.
 */
const cleanStringFormSpaces = (value: string): string => {
  return value.replaceAll(' ', '');
};

/**
 * The `_luhnCheck` method is used to calculate card number by 'Luhn' algorithm.
 *
 * @param {string} pan - The card number string to be checked.
 * @returns {boolean} - Returns card number validation result.
 */
const luhnCheck = (pan: string): boolean => {
  const arr = [...pan].reverse().map((x) => Number.parseInt(x));
  const lastDigit = arr.shift();
  let sum = arr.reduce(
    (acc, val, i) =>
      i % 2 === 0 ? acc + ((val *= 2) > 9 ? val - 9 : val) : acc + val,
    0,
  );
  sum += lastDigit;

  return sum % 10 === 0;
};

export const limitIntegerDigits = (value: string, maxDigits = 12): string => {
  if (!value) return value;
  let normalizedValue = value.replace(/,/g, '.');

  if (normalizedValue.trim() === '.') {
    normalizedValue = '0.';
  }

  const match = normalizedValue.match(/[.]/);
  const sep = match ? match[0] : '';
  const [integer = '', decimals] = normalizedValue.split(/[.]/);

  const trimmedInt = integer.slice(0, maxDigits);

  return decimals !== undefined ? `${trimmedInt}${sep}${decimals}` : trimmedInt;
};

const phoneRegExp = new RegExp(
  String.raw`^((00|\+)[(,),-.]*)?((?:9[679]|8[035789]|6[789]|5[90]|42|3[578]|2[1-689])|9[0-58]|8[1246]|6[0-6]|5[1-8]|4[013-9]|3[0-469]|2[70]|7|1)[(,),-.]*(?:\W*\d[(,),-.]*){8,12}\d$`,
);

export const phoneValidator = (): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (value && !phoneRegExp.test(value)) {
      return { phoneInvalid: true };
    }
    return null;
  };
};

export const cleanPhoneNumber = (phone: string): string => {
  return phone.replaceAll(/[^0-9+]/g, '');
};
